/**
 * 売上要約（R-03 / R03-C-3）について「**何を出すか**」だけを決める。
 *
 * ★このファイルは **DOM を触らない**（`targets-view.ts` と同じ理由）。
 *   判断を描画関数の中に書くと、その判断を壊しても `tsc` もテストも落ちない。
 *
 * ## ★ここが担っている判断：`null` を 0 に見せない
 *
 * 応答の `null` には4つの意味がある。**どれも「0」と出した瞬間に区別が消える。**
 *   - 当日の行が無い       … 「未入力」（`recorded: false`）
 *   - 目標が未設定         … 「未設定」（`targetSales` / `targetGuests` が `null`）
 *   - 目標が 0             … 「—（目標0）」（達成率だけ `null`）
 *   - カレンダー未整備     … 「カレンダー未整備」（`calendarUnavailableReason`）
 *
 * 数値の書式（桁区切り・%）はここで確定する。`docs/02` §8「表示時のみ書式変換」。
 */
import type { SalesSummary } from "./api.js";

export const UNRECORDED = "未入力";
export const UNSET = "未設定";
export const NO_CALENDAR = "カレンダー未整備";

export interface SummaryLine {
  label: string;
  value: string;
  /**
   * 達成率（1.0 = 目標ちょうど）。**棒を引ける行にだけ入る。**
   *
   * ★`value` に百分率が出る行と1:1。★「未入力」「未設定」「—（目標0）」の行には入れない
   *   ——**棒が無いこと自体が「まだ読めない」の合図**で、0% の棒と区別が付かなくなる。
   */
  ratio?: number;
}

const yen = (n: number): string => `${Math.round(n).toLocaleString()}円`;
const people = (n: number): string => `${n.toLocaleString()}人`;

/**
 * 達成率の行。★4つの状態を1箇所で言い分ける（目標未設定／未入力／目標0／達成率）。
 *
 * ★**この順序が意味を決める。** 目標が無ければ実績の有無を問わず「未設定」——
 *   先に「未入力」と言うと、目標を入れれば読めるようになる行が「入力待ち」に化ける。
 */
function achievement(
  label: string,
  value: number | null,
  target: number | null,
  recorded: boolean,
  unsetLabel: string,
): SummaryLine {
  if (target === null) return { label, value: unsetLabel };
  if (!recorded) return { label, value: UNRECORDED };
  if (value === null) return { label, value: "—（目標0）" };
  return { label, value: `${(value * 100).toFixed(1)}%`, ratio: value };
}

/** 棒の幾何（%）。★描画は `main.ts`。ここは「どこまで伸ばし、目標がどこか」だけを決める */
export interface BarGeometry {
  /** 実績の長さ */
  fill: number;
  /** 目標の位置。★未達なら常に右端（100）＝**枠そのものが目標** */
  target: number;
  /** 目標を超えた */
  over: boolean;
}

/**
 * ★**超えたぶんだけ目盛りを縮める。** 上限で頭打ちにすると 105% と 300% が同じ絵になり、
 *   「グラフで意味が分かる」という intent（R-03）を棒が裏切る。
 */
export function barGeometry(ratio: number): BarGeometry {
  const scale = Math.max(ratio, 1);
  return { fill: (ratio / scale) * 100, target: (1 / scale) * 100, over: ratio > 1 };
}

export interface SummaryView {
  /** 当日の見出し */
  dayHeading: string;
  day: SummaryLine[];
  /** 当月の見出し。★入力済みの日数を必ず含める（0日でも黙らない） */
  monthHeading: string;
  month: SummaryLine[];
}

export function summaryView(s: SalesSummary): SummaryView {
  const { day, month } = s;
  const d = (v: string): string => (day.recorded ? v : UNRECORDED);
  // ★当月も、1日も入っていなければ「未入力」。合計 0 は正しいが、0円と見せると「売れなかった月」に化ける
  const recordedMonth = month.recordedDays !== 0;
  const m = (v: string): string => (recordedMonth ? v : UNRECORDED);
  // ★日別客数目標が無い理由は2つ同時に真になりうる（月間客数目標が未設定／カレンダー未整備）。
  //   先に直せるのは目標の入力なので「未設定」を優先し、目標があるのに出ないときだけ「カレンダー未整備」
  const guestsTargetLabel = month.targetGuests === null ? UNSET : NO_CALENDAR;
  return {
    dayHeading: `当日 ${s.date}`,
    day: [
      { label: "売上（税抜）", value: d(yen(day.exclTax)) },
      { label: "売上（税込）", value: d(yen(day.inclTax)) },
      { label: "客数", value: d(people(day.guests)) },
      { label: "客単価", value: d(day.perGuest === null ? "—（客数0）" : yen(day.perGuest)) },
      achievement("売上目標達成率", day.salesAchievement, day.targetSales, day.recorded, UNSET),
      achievement(
        "客数目標達成率",
        day.guestsAchievement,
        day.targetGuests,
        day.recorded,
        guestsTargetLabel,
      ),
    ],
    monthHeading:
      `当月累計 ${s.yearMonth}` +
      (month.recordedDays === 0 ? "（入力済みの日はありません）" : `（${month.recordedDays}日ぶん入力済み）`),
    month: [
      { label: "累計売上（税抜）", value: m(yen(month.exclTax)) },
      { label: "累計売上（税込）", value: m(yen(month.inclTax)) },
      { label: "累計客数", value: m(people(month.guests)) },
      { label: "客単価", value: m(month.perGuest === null ? "—（客数0）" : yen(month.perGuest)) },
      {
        label: "日商平均",
        value:
          month.calendarUnavailableReason !== null
            ? NO_CALENDAR
            : m(month.dailyAverage === null ? "—（営業日0）" : yen(month.dailyAverage)),
      },
      achievement("売上目標達成率", month.salesAchievement, month.targetSales, recordedMonth, UNSET),
      achievement("客数目標達成率", month.guestsAchievement, month.targetGuests, recordedMonth, UNSET),
    ],
  };
}
